import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@components/Tooltip';

const mobileQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;

const subscribe = callback => {
    mobileQuery?.addEventListener('change', callback);
    return () => mobileQuery?.removeEventListener('change', callback);
};

const getSnapshot = () => mobileQuery?.matches ?? false;

const swallowClick = event => event.stopPropagation();

const ActionIconButton = ({
    title,
    onClick,
    onMouseLeave,
    onBlur,
    disabled,
    loading = false,
    children,
    component: Component = IconButton,
    ...rest
}) => {
    const isMobile = useSyncExternalStore(subscribe, getSnapshot);

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const tooltipDismissed = useRef(false);

    useEffect(() => {
        setTooltipOpen(false);
    }, [loading]);

    const handleClick = event => {
        tooltipDismissed.current = true;
        setTooltipOpen(false);
        if (!loading) onClick?.(event);
    };

    const button = (
        <Component
            onClick={handleClick}
            onMouseLeave={event => {
                tooltipDismissed.current = false;
                onMouseLeave?.(event);
            }}
            onBlur={event => {
                tooltipDismissed.current = false;
                onBlur?.(event);
            }}
            disabled={disabled}
            aria-label={title}
            aria-disabled={loading || undefined}
            {...rest}
        >
            {children}
        </Component>
    );

    if (isMobile) return button;

    return (
        <Tooltip
            title={title}
            placement="top"
            open={tooltipOpen && !loading}
            onOpen={() => {
                if (!loading && !tooltipDismissed.current) setTooltipOpen(true);
            }}
            onClose={() => setTooltipOpen(false)}
        >
            {disabled ? <span onClick={swallowClick}>{button}</span> : button}
        </Tooltip>
    );
};

export default ActionIconButton;
